/**
 * This component wraps React-Router's Link by providing name-based routing.
 *
 * The `name` prop should match a route in the flattened
 * routeConfiguration passed in context by the RoutesProvider
 * component. The `params` props is the route params for the route
 * path of the given route name.
 *
 * The `to` prop is an object with the same shape as Link requires,
 * but without `pathname` that will be generated from the given route
 * name.
 *
 * Some additional props can be passed for the <a> element like
 * `className` and `style`.
 *
 * The component can also be given the `activeClassName` prop that
 * will be added to the element className if the current URL matches
 * the one in the generated pathname of the link.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import routeConfiguration from '../../routeConfiguration';
import { pathByRouteName } from '../../util/routes';
import { preloadPageForRoute } from '../../util/pagePreload';

export const NamedLinkComponent = props => {
  const {
    to,
    name,
    params,
    title,
    target,
    rootClassName,
    className,
    style,
    activeClassName,
    match,
    history,
    location,
    staticContext,
    children,
    onMouseOver,
    onTouchStart,
    ...otherAElemProps
  } = props;

  // Link props
  const pathname = pathByRouteName(name, routeConfiguration(), params);
  const active = match.url && match.url === pathname;

  // <a> element props
  const aElemProps = {
    className: classNames(rootClassName, className, { [activeClassName]: active }),
    style,
    title,
  };

  if (target) {
    aElemProps.target = target;

    if (target === '_blank') {
      aElemProps.rel = 'noopener noreferrer';
    }
  }

  const handlePreload = event => {
    preloadPageForRoute(name);
    if (typeof event.persist === 'function') {
      event.persist();
    }
  };

  const handleMouseOver = event => {
    handlePreload(event);
    onMouseOver(event);
  };

  const handleTouchStart = event => {
    handlePreload(event);
    onTouchStart(event);
  };

  return (
    <Link
      to={{ pathname, ...to }}
      {...aElemProps}
      {...otherAElemProps}
      onMouseOver={handleMouseOver}
      onTouchStart={handleTouchStart}
    >
      {children}
    </Link>
  );
};

const { object, string, shape, any, func } = PropTypes;

NamedLinkComponent.defaultProps = {
  params: {},
  to: {},
  children: null,
  rootClassName: '',
  className: '',
  style: {},
  activeClassName: 'NamedLink_active',
  title: null,
  match: {},
  onClick: () => {},
  onMouseOver: () => {},
  onTouchStart: () => {},
  target: null,
};

// This ensures a nice display name in snapshots etc.
NamedLinkComponent.displayName = 'NamedLink';

NamedLinkComponent.propTypes = {
  // name of the route in routeConfiguration
  name: string.isRequired,
  // params object for the named route
  params: object,
  // Link component props
  to: shape({ search: string, hash: string, state: object }),
  children: any,

  // generic props for the underlying <a> element
  rootClassName: string,
  className: string,
  style: object,
  activeClassName: string,
  title: string,

  // from withRouter
  match: object,

  onClick: func,
  onMouseOver: func,
  onTouchStart: func,
  target: string,
};

const NamedLink = withRouter(NamedLinkComponent);
NamedLink.displayName = 'NamedLink';

export default NamedLink;
